import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Plus, Pencil, Trash2, CheckCircle2, X, Star } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import {
  apiListAddresses,
  apiCreateAddress,
  apiUpdateAddress,
  apiDeleteAddress,
} from '../services/auth';

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Delhi','Goa','Gujarat',
  'Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra',
  'Manipur','Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim',
  'Tamil Nadu','Telangana','Tripura','Uttar Pradesh','Uttarakhand','West Bengal',
];

const EMPTY = {
  first_name: '', last_name: '', phone: '', email: '',
  address_line1: '', address_line2: '',
  city: '', state: '', pincode: '', country: 'India',
  is_default: false,
};

/**
 * Renders both a card grid of saved addresses AND a controlled create/edit form.
 * When `onSelect` is passed (checkout flow) it emits an address the user picks;
 * otherwise it's a full management screen for /account/addresses.
 *
 * NOTE: The create/edit modal intentionally does NOT use an HTML <form> element.
 * This component is designed to be embeddable inside a parent <form> (e.g. the
 * Checkout page). Nested <form> elements are invalid HTML — browsers will not
 * reliably route submit events to the correct handler, which silently breaks
 * the "Save address" button (clicks can bubble to the outer form's onSubmit
 * instead of this component's save logic, with no API call ever firing).
 * All submission here is driven by an explicit button onClick + manual
 * validation instead of form onSubmit / required attributes.
 */
export default function AddressBook({ onSelect, selectedId }) {
  const { user } = useAuth();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [form, setForm]           = useState(null);      // null | EMPTY | address to edit
  const [error, setError]         = useState('');
  const [busy, setBusy]           = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await apiListAddresses(user?.id);
      setAddresses(Array.isArray(list) ? list : []);
    } catch (e) { setError(e.message || 'Could not load addresses'); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [user?.id]);

  const change = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    if (!form.first_name?.trim())    return 'First name is required.';
    if (!form.address_line1?.trim()) return 'Address line 1 is required.';
    if (!form.city?.trim())          return 'City is required.';
    if (!form.state?.trim())         return 'Please select a state.';
    if (!/^\d{6}$/.test(form.pincode || '')) return 'Enter a valid 6-digit Indian PIN code.';
    if (!/^\d{10}$/.test(form.phone || ''))  return 'Enter a valid 10-digit mobile number.';
    return '';
  };

  const save = async () => {
    setError('');
    const validationError = validate();
    if (validationError) { setError(validationError); return; }

    setBusy(true);
    try {
      if (form.id) await apiUpdateAddress(user?.id, form.id, form);
      else         await apiCreateAddress(user?.id, form);
      setForm(null);
      await load();
    } catch (e) { setError(e.message || 'Could not save address.'); }
    finally { setBusy(false); }
  };

  // Allow Enter key inside text inputs to trigger save, same as a form would,
  // without actually being inside a <form>.
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
      save();
    }
  };

  const remove = async (id) => {
    if (! window.confirm('Delete this address?')) return;
    try {
      await apiDeleteAddress(user?.id, id);
      await load();
    } catch (e) { setError(e.message || 'Could not delete address.'); }
  };

  const setDefault = async (a) => {
    try {
      await apiUpdateAddress(user?.id, a.id, { ...a, is_default: true });
      await load();
    } catch (e) { setError(e.message || 'Could not set default address.'); }
  };

  return (
    <div className="space-y-4" data-testid="address-book">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl text-neutral-900">
          {onSelect ? 'Choose a delivery address' : 'Saved addresses'}
        </h2>
        <button
          type="button"
          onClick={() => setForm({ ...EMPTY, is_default: addresses.length === 0 })}
          data-testid="add-address-btn"
          className="inline-flex items-center gap-2 bg-[#1e3a2b] hover:bg-[#2a5040] text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
        >
          <Plus className="w-4 h-4" /> Add address
        </button>
      </div>

      {error && !form && <div className="text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2">{error}</div>}

      {loading ? (
        <div className="bg-white rounded-xl border p-6 text-neutral-500">Loading…</div>
      ) : addresses.length === 0 && !form ? (
        <div className="bg-white rounded-xl border p-10 text-center">
          <MapPin className="w-10 h-10 mx-auto text-neutral-400" />
          <h3 className="mt-3 font-serif text-xl">No addresses yet</h3>
          <p className="text-sm text-neutral-600 mt-1">Add one to speed up your next checkout.</p>
          <button
            type="button"
            onClick={() => setForm({ ...EMPTY, is_default: true })}
            className="inline-flex items-center gap-2 mt-4 bg-[#1e3a2b] hover:bg-[#2a5040] text-white px-6 py-2.5 rounded-md font-semibold"
          >
            <Plus className="w-4 h-4" /> Add first address
          </button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {addresses.map(a => {
            const isSelected = onSelect && selectedId === a.id;
            return (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                data-testid={`address-card-${a.id}`}
                onClick={() => onSelect?.(a)}
                className={`bg-white rounded-xl border p-5 relative transition-shadow ${
                  isSelected ? 'border-[#1e3a2b] ring-2 ring-[#1e3a2b]/20' : 'border-neutral-200 hover:border-neutral-400'
                } ${onSelect ? 'cursor-pointer' : ''}`}
              >
                {a.is_default && (
                  <span className="absolute -top-2 left-4 bg-[#b8935a] text-white text-[10px] font-bold tracking-widest px-2 py-0.5 rounded flex items-center gap-1">
                    <Star className="w-3 h-3" /> DEFAULT
                  </span>
                )}
                {isSelected && (
                  <CheckCircle2 className="absolute top-3 right-3 w-5 h-5 text-[#1e3a2b]" />
                )}
                <div className="text-sm">
                  <div className="font-semibold text-neutral-900">{a.first_name} {a.last_name}</div>
                  <div className="text-neutral-700 mt-0.5">{a.phone}{a.email ? ` • ${a.email}` : ''}</div>
                  <div className="mt-2 text-neutral-800 leading-relaxed">
                    {a.address_line1}{a.address_line2 ? `, ${a.address_line2}` : ''},<br />
                    {a.city}, {a.state} – <b>{a.pincode}</b><br />
                    {a.country || 'India'}
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-1.5 text-xs">
                  {!a.is_default && (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setDefault(a); }}
                      data-testid={`set-default-${a.id}`}
                      className="px-2.5 py-1 border border-neutral-300 rounded hover:bg-neutral-50"
                    >
                      Set default
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setForm(a); }}
                    data-testid={`edit-address-${a.id}`}
                    className="px-2.5 py-1 border border-neutral-300 rounded hover:bg-neutral-50 inline-flex items-center gap-1"
                  >
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); remove(a.id); }}
                    data-testid={`delete-address-${a.id}`}
                    className="px-2.5 py-1 border border-red-200 text-red-600 rounded hover:bg-red-50 inline-flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal create/edit panel.
          IMPORTANT: this is a <div>, not a <form>. AddressBook can be mounted
          inside another component's <form> (see Checkout.jsx), and nested
          <form> elements are invalid HTML that silently break submit routing.
          Saving is triggered explicitly via the button's onClick + handleKeyDown,
          not via form submission. */}
      <AnimatePresence>
        {form && (
          <motion.div
            className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => !busy && setForm(null)}
            data-testid="address-form-modal"
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              onKeyDown={handleKeyDown}
              initial={{ opacity: 0, y: 20, scale: 0.98 }}
              animate={{ opacity: 1, y: 0,  scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              className="bg-white w-full max-w-2xl rounded-xl shadow-2xl p-6 max-h-[90vh] overflow-y-auto"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-serif text-2xl text-neutral-900">{form.id ? 'Edit address' : 'Add new address'}</h3>
                <button type="button" onClick={() => setForm(null)} className="p-2 rounded-full hover:bg-neutral-100" aria-label="Close">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {error && <div className="mb-4 text-sm bg-red-50 border border-red-200 text-red-700 rounded-md px-3 py-2">{error}</div>}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-neutral-600">First name *</label>
                  <input value={form.first_name} onChange={(e) => change('first_name', e.target.value)} data-testid="addr-first-name" className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600">Last name</label>
                  <input value={form.last_name || ''} onChange={(e) => change('last_name', e.target.value)} data-testid="addr-last-name" className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600">Mobile *</label>
                  <input inputMode="numeric" pattern="[0-9]{10}" placeholder="10-digit mobile" value={form.phone} onChange={(e) => change('phone', e.target.value.replace(/\D/g, ''))} data-testid="addr-phone" className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600">Email</label>
                  <input type="email" value={form.email || ''} onChange={(e) => change('email', e.target.value)} data-testid="addr-email" className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-neutral-600">Address line 1 *</label>
                  <input value={form.address_line1} onChange={(e) => change('address_line1', e.target.value)} data-testid="addr-line1" placeholder="Flat, House no., Building" className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-medium text-neutral-600">Address line 2</label>
                  <input value={form.address_line2 || ''} onChange={(e) => change('address_line2', e.target.value)} data-testid="addr-line2" placeholder="Area, Street, Sector, Village" className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600">City *</label>
                  <input value={form.city} onChange={(e) => change('city', e.target.value)} data-testid="addr-city" className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600">State *</label>
                  <select value={form.state} onChange={(e) => change('state', e.target.value)} data-testid="addr-state" className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 bg-white focus:outline-none focus:border-[#1e3a2b]">
                    <option value="">Select state</option>
                    {INDIAN_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600">PIN code *</label>
                  <input inputMode="numeric" pattern="[0-9]{6}" maxLength={6} placeholder="6-digit PIN" value={form.pincode} onChange={(e) => change('pincode', e.target.value.replace(/\D/g, ''))} data-testid="addr-pincode" className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2.5 focus:outline-none focus:border-[#1e3a2b]" />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600">Country</label>
                  <input disabled value="India" className="mt-1 w-full border border-neutral-200 rounded-md px-3 py-2.5 bg-neutral-50 text-neutral-500" />
                  <p className="mt-1 text-[11px] text-neutral-500">We currently ship within India only.</p>
                </div>
              </div>

              <label className="mt-4 flex items-center gap-2 text-sm text-neutral-800 cursor-pointer">
                <input type="checkbox" checked={!!form.is_default} onChange={(e) => change('is_default', e.target.checked)} data-testid="addr-default" className="w-4 h-4 accent-[#1e3a2b]" />
                Make this my default delivery address
              </label>

              <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                <button type="button" onClick={() => setForm(null)} className="px-5 py-2.5 rounded-md border border-neutral-300 hover:bg-neutral-50 text-neutral-800 font-medium">Cancel</button>
                <button
                  type="button"
                  onClick={save}
                  disabled={busy}
                  data-testid="save-address-btn"
                  className="px-5 py-2.5 rounded-md bg-[#1e3a2b] hover:bg-[#2a5040] disabled:opacity-60 text-white font-semibold"
                >
                  {busy ? 'Saving…' : (form.id ? 'Save changes' : 'Save address')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
